import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, ArrowRight } from "lucide-react";
import { getEvents } from "@/lib/api/events"; 
import type { EventData } from "@/types/events";
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext"; 

const EventsSection = () => {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage(); 

   useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await getEvents({ language,filters: { upcoming: true }, pageSize: 3 });
        setEvents(res.data.slice(0, 3)); 
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [language]);
  
  return (
    <section className="py-16 bg-white dark:bg-nexus-dark" id="events">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-nexus-gray dark:text-nexus-light mb-4">{t("events.pageTitle")}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t("events.pageDescription")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {events.map((event, index) => (
            <Card key={index} className="border border-gray-200 transition-all hover:shadow-md">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <Badge className={`${
                    event.type.en === "Webinar" ? "bg-nexus-teal/20 text-nexus-teal" :
                    event.type.en === "Workshop" ? "bg-nexus-yellow/20 text-nexus-gray" :
                    "bg-nexus-red/20 text-nexus-red"
                  }`}>
                    {event.type[language]}
                  </Badge>
                  {event.badge && (
                    <Badge className={`${
                      event.badge === "Popular" ? "bg-nexus-yellow text-nexus-gray" :
                      event.badge === "New" ? "bg-nexus-red text-white" :
                      "bg-gray-500 text-white"
                    }`}>
                      {event.badge}
                    </Badge>
                  )}
                </div>

                <h3 className="text-xl font-semibold text-nexus-gray dark:text-nexus-light mb-2">{event.title}</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-1">{event.speaker?.name}</p>

                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                  <Calendar className="h-4 w-4" />
                  <span>{event.date}, {event.time}</span>
                </div>

                {event.location && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <MapPin className="h-4 w-4" />
                    <span>{event.location}</span>
                  </div>
                )}

                <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{event.attendees} attending</span>
                  </div>
                  {event.GoogleFormURL ? (
                    <div className="text-nexus-teal hover:text-nexus-teal/80 font-medium text-sm">
                      <a href={event.GoogleFormURL} target="_blank" rel="noopener noreferrer">
                        {t("events.register")}
                      </a>
                    </div>
                  ) : (
                    <Button disabled size="sm" className="bg-gray-300 text-gray-600">
                      {t("events.registerUnavailable")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90 dark:text-white">
            <Link to="/events">
              {language === 'ar' ? (
                  <>
                    {t("events.viewAll")}
                    <ArrowRight className="mr-2 h-4 w-4 transform rotate-180" />
                  </>
                ) : (
                  <>
                    {t("events.viewAll")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
              )}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
